import { ICartUpdatePayload } from "@/api_framework/api_modals/Cart";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import SearchableCouponSelectMenu from "@/app/components/ui/SelectMenus/SearchableCouponSelectMenu";
import { useCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useCartDetailsAPI";
import { useCartItemDeleteAPI } from "@/app/hooks/api_hooks/Cart/useCartItemDeleteAPI";
import { useUpdateCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useUpdateCartDetailsAPi";
import { useCartLevelCouponListAPI } from "@/app/hooks/api_hooks/Cart/useCartLevelCouponListAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const CartHDTable = ({
  item,
  key,
  min,
  activeCartDetails,
  activeCartId,
  mappedClinicId,
  currentAddress,
  cartLevelCouponId,
  customerId,
  petId,
  mappedClinicName,
  userLogInfo,
}) => {
  const [localQty, setLocalQty] = useState(item.quantity);
  const [_isUpdating, setIsUpdating] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState<string | undefined>(currentAddress?.pincode);
  const [searchParams] = useSearchParams();
  const petIdFromParams = searchParams.get("pet_id") || "";
  const { execute: updateCartDetails } = useUpdateCartDetailsAPI();
  const { execute: refetchCartDetails } = useCartDetailsAPI();
  const { execute: deleteCartItem } = useCartItemDeleteAPI();
  const { execute: logAPI } = useClickLogAPI();
  const { execute: fetchCartLevelCoupons } = useCartLevelCouponListAPI();

  useEffect(() => {
    if (currentAddress?.pincode) {
      setSelectedPincode(currentAddress?.pincode);
    }
  }, [currentAddress]);

  // Keep the input in sync when the quantity changes server-side (e.g. Sync EMR refresh);
  // localQty is only seeded on mount, so without this it would show the previous value.
  useEffect(() => {
    setLocalQty(item.quantity);
  }, [item.quantity]);

  const callLogAPIBase = (
    item: string,
    eventType: "CART_QUANTITY_PLUS" | "CART_QUANTITY_MINUS" | "CART_COUPON" | "CART_ITEM_DELETED",
  ) => {
    const logBody: IClickLogBody = {
      event_type: eventType,
      scr_id: "CART",
      client_id: customerId,
      emr_id: activeCartDetails?.emr_id,
      cart_id: activeCartId,
      pet_id: petId,
      emr_cart_items: activeCartDetails,
      emr_cart_click_item: item,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userLogInfo,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const callLogAPILab = (item: any) => callLogAPIBase(item, "CART_QUANTITY_PLUS");
  const callLogAPILabMINUS = (item: any) => callLogAPIBase(item, "CART_QUANTITY_MINUS");
  const callLogAPILabDeleted = (item: any) => callLogAPIBase(item, "CART_ITEM_DELETED");

  const handleQtyChange = async (updatedQty: number, item: any) => {
    setIsUpdating(true);
    try {
      if (!activeCartDetails || !activeCartId || !mappedClinicId) return;
      const payload: ICartUpdatePayload = {
        customer_id: activeCartDetails.customer_id,
        pincode: selectedPincode,
        is_hd_stock: true,
        cart_id: activeCartId,
        clinic_id: mappedClinicId,
        main_cart_items: [
          {
            vetic_variant_id: item.vetic_variant_id,
            coupon_id: item.coupon_id || "",
            quantity: updatedQty,
            unique_row_id: item.unique_row_id,
            unit_price: item.unit_price,
          },
        ],
        pets: [
          {
            pet_id: petIdFromParams,
            items: [
              {
                id: item.id,
                qty: updatedQty,
                coupon_id: item.coupon_id || "",
                tag: item.tag || "",
                availability: item.availability || "",
                unique_row_id: item.unique_row_id || "",
              },
            ],
          },
        ],
      };
      await updateCartDetails(payload, "hd");
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
      refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, selectedPincode);
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
    } catch (err) {
      console.error("Error updating HD cart quantity:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCouponChange = async (couponId: string, item: any) => {
    setIsUpdating(true);
    try {
      if (!activeCartDetails || !activeCartId || !mappedClinicId) return;
      const payload: ICartUpdatePayload = {
        customer_id: activeCartDetails.customer_id,
        pincode: currentAddress?.pincode,
        is_hd_stock: true,
        cart_id: activeCartId,
        clinic_id: mappedClinicId,
        main_cart_items: [
          {
            vetic_variant_id: item.vetic_variant_id,
            coupon_id: couponId || "",
            quantity: item.quantity,
            unique_row_id: item.unique_row_id,
            unit_price: item.unit_price,
          },
        ],
        pets: [
          {
            pet_id: petIdFromParams,
            items: [
              {
                id: item.id,
                qty: item.qty,
                coupon_id: couponId || "",
                tag: item.tag || "",
                availability: item.availability || "",
                unique_row_id: item.unique_row_id || "",
              },
            ],
          },
        ],
      };
      await updateCartDetails(payload, "hd");
      refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, currentAddress?.pincode);
    } catch (err) {
      console.error("Error updating coupon in HD cart:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeletePetCartRow = (rowItemId: string, urid: string) => {
    deleteCartItem(
      {
        cart_id: activeCartId,
        item_id: rowItemId,
        unique_row_id: urid,
        pet_id: petIdFromParams,
        clinic_id: mappedClinicId,
        customer_id: customerId,
        is_hd_stock: true,
        pincode: currentAddress?.pincode,
      },
      "hd",
    ).then(() => {
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
      refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, currentAddress?.pincode).then(() => {
        callLogAPILabDeleted(rowItemId);
      });
      fetchCartLevelCoupons(activeCartId, mappedClinicId);
    });
  };

  return (
    <>
      <tr key={key} className="border-b sm:text-[11px] text-[12px]">
        <td className="p-3">
          <p>{item?.name}</p>
          <div className="flex justify-start items-center gap-x-2">
            {item.source === 2 && (
              <SimpleBadge label={"EMR"} colorClassName={clsx("text-[#F75C03] bg-[#FFE7D9] ring-orange-500/10")} />
            )}
            {item.is_booking_addon === true && (
              <SimpleBadge label={"App"} colorClassName={clsx("text-[#F75C03] bg-[#FFE7D9] ring-orange-500/10")} />
            )}
          </div>
        </td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <div className="relative pb-3">
              <InputWithStartAndEndAddOn
                className={`h-6 text-center ${item?.is_disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                startAddon="-"
                endAddon="+"
                step={1}
                min={min}
                type="string"
                disabled={item?.is_disabled}
                value={localQty}
                startAddOnCallback={() => {
                  if (!item?.is_disabled && localQty > min) {
                    handleQtyChange(item.quantity - 1, item);
                  }
                  callLogAPILabMINUS(item.row_id);
                }}
                endAddOnCallback={() => {
                  if (!item?.is_disabled) {
                    handleQtyChange(item.quantity + 1, item);
                  }
                  callLogAPILab(item.row_id);
                }}
                onChange={(e: any) => {
                  if (!item?.is_disabled) {
                    const value = e.target.value;
                    if (value === "" || /^\d*$/.test(value)) {
                      setLocalQty(value);
                    }
                  }
                }}
                onBlur={(e: any) => {
                  if (!item?.is_disabled) {
                    let value = parseInt(e.target.value) || min;
                    if (value < min) value = min;
                    setLocalQty(value);
                    if (value !== item.quantity) {
                      handleQtyChange(value, item);
                    }
                  }
                }}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter" && !item?.is_disabled) {
                    e.preventDefault();
                    let value = parseInt(e.target.value) || min;
                    if (value < min) value = min;
                    setLocalQty(value);
                    if (value !== item.quantity) {
                      handleQtyChange(value, item);
                    }
                  }
                }}
              />
            </div>
          </div>
        </td>
        <td className="p-3">{item?.unit_price}</td>
        <td className="p-3">{item?.gross_price}</td>
        <td className="p-3">
          <SearchableCouponSelectMenu
            errorMessages={[]}
            onSelectItem={(coupon) => handleCouponChange(coupon ? coupon.id : "", item)}
            selectItems={
              item?.all_applicable_coupons?.map((coupon) => ({
                id: coupon.coupon_id,
                title: coupon.coupon_name,
              })) || []
            }
            defaultSelected={
              item?.all_applicable_coupons
                ?.filter((coupon) => coupon.is_selected)
                .map((coupon) => ({
                  id: coupon.coupon_id,
                  title: coupon.coupon_name,
                }))[0] || null
            }
            fieldError={undefined}
            register={undefined}
            optionRenderVariant="DEFAULT"
            placeholder="Select Coupon"
            showTooltips={true}
            showDropdownIcon={true}
            withReset={true}
            listBoxClassName="w-full"
            className="text-gray-800 w-10"
            containerClassName="w-full"
            disabled={item?.is_disabled}
          />
          {item?.selected_coupon && <div className="text-[#00AA4C]">{item?.selected_coupon}</div>}
        </td>
        <td className="p-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{item?.net_price}</span>

            <div className="bg-green-50 border border-[#00AA4C] text-[#00AA4C] px-2 rounded-md sm:text-[8px] text-[10px] font-medium whitespace-nowrap">
              {item?.discount_text}
            </div>
          </div>
        </td>
        <td className="p-3 text-center">
          <button
            disabled={item?.is_disabled}
            onClick={() => handleDeletePetCartRow(item?.vetic_variant_id, item.unique_row_id)}
            className={`${item?.is_disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-500 hover:text-red-500"}`}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </td>
      </tr>
    </>
  );
};

export default CartHDTable;
